import { collection, getDocs, query, where, orderBy, doc, getDoc, setDoc, updateDoc, getCountFromServer } from 'firebase/firestore'
import { db } from './firebase'
import groupAccessService from './groupAccessService'

class GroupService {
  /**
   * Count users following a group (followers subcollection matches adminService.followGroup paths).
   */
  async getGroupFollowerCount(organizationId, group) {
    const orgIdString = typeof organizationId === 'string' ? organizationId : String(organizationId)
    const segments = [...new Set(
      [group.id, group.name]
        .filter(Boolean)
        .map((s) => String(s).trim())
        .filter(Boolean)
    )]
    if (segments.length === 0) return typeof group.followerCount === 'number' ? group.followerCount : 0

    let best = typeof group.followerCount === 'number' ? group.followerCount : 0
    for (const segment of segments) {
      try {
        const followersRef = collection(db, 'organizations', orgIdString, 'groups', segment, 'followers')
        const snap = await getCountFromServer(followersRef)
        best = Math.max(best, snap.data().count)
      } catch (error) {
        console.warn('⚠️ GroupService: follower count failed for', orgIdString, segment, error)
      }
    }
    return best
  }

  async attachFollowerCountsToGroups(organizationId, groups) {
    if (!groups?.length) return groups
    return Promise.all(
      groups.map(async (group) => ({
        ...group,
        followerCount: await this.getGroupFollowerCount(organizationId, group)
      }))
    )
  }

  // Fetch all groups for a specific organization
  async getGroupsForOrganization(organizationId, userId = null, userProfile = null) {
    try {
      // Ensure organizationId is a string
      const orgIdString = typeof organizationId === 'string' ? organizationId : String(organizationId)
      
      console.log('🔍 GroupService: Fetching groups for organization:', orgIdString)
      console.log('🔍 GroupService: User ID for access control:', userId)
      console.log('🔍 GroupService: User profile for admin check:', userProfile)
      console.log('🔍 GroupService: Querying path: /organizations/' + orgIdString + '/groups')
      
      // Use a simple query without composite index requirements
      const groupsQuery = query(
        collection(db, 'organizations', orgIdString, 'groups')
      )
      
      const groupsSnapshot = await getDocs(groupsQuery)
      console.log('🔍 GroupService: Raw groups snapshot size:', groupsSnapshot.docs.length)
      
      let groups = groupsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      
      console.log('🔍 GroupService: All groups:', groups)
      
      // Filter active groups in JavaScript (no index needed)
      const activeGroups = groups.filter(group => group.isActive !== false)
      
      console.log('🔍 GroupService: Active groups:', activeGroups)
      
      // Apply privacy controls - filter based on user access
      const accessibleGroups = await groupAccessService.filterAccessibleGroups(userId, orgIdString, activeGroups, userProfile)
      
      console.log('🔍 GroupService: Accessible groups after privacy filter:', accessibleGroups)
      
      // Sort by name in JavaScript (no index needed)
      accessibleGroups.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      
      console.log('🔍 GroupService: Final sorted groups:', accessibleGroups)

      return await this.attachFollowerCountsToGroups(orgIdString, accessibleGroups)
    } catch (error) {
      console.error('❌ GroupService: Error fetching groups:', error)
      return []
    }
  }

  // Fetch a specific group by ID
  async getGroupById(organizationId, groupId) {
    try {
      const groupDoc = await getDoc(doc(db, 'organizations', organizationId, 'groups', groupId))
      
      if (groupDoc.exists()) {
        return {
          id: groupDoc.id,
          ...groupDoc.data()
        }
      }
      
      return null
    } catch (error) {
      console.error('Error fetching group:', error)
      return null
    }
  }

  // Create a new group
  async createGroup(organizationId, groupData) {
    try {
      const groupRef = doc(collection(db, 'organizations', organizationId, 'groups'))
      const newGroup = {
        id: groupRef.id,
        name: groupData.name,
        description: groupData.description || '',
        organizationId: organizationId,
        isActive: true,
        memberCount: 0,
        createdAt: new Date(),
        updatedAt: new Date()
      }
      
      await setDoc(groupRef, newGroup)
      return newGroup
    } catch (error) {
      console.error('Error creating group:', error)
      throw error
    }
  }

  // Update an existing group
  async updateGroup(organizationId, groupId, updateData) {
    try {
      const groupRef = doc(db, 'organizations', organizationId, 'groups', groupId)
      const updatePayload = {
        ...updateData,
        updatedAt: new Date()
      }
      
      await updateDoc(groupRef, updatePayload)
      return true
    } catch (error) {
      console.error('Error updating group:', error)
      throw error
    }
  }

  // Delete a group (soft delete by setting isActive to false)
  async deleteGroup(organizationId, groupId) {
    try {
      const groupRef = doc(db, 'organizations', organizationId, 'groups', groupId)
      await updateDoc(groupRef, {
        isActive: false,
        updatedAt: new Date()
      })
      
      return true
    } catch (error) {
      console.error('Error deleting group:', error)
      throw error
    }
  }
}

export default new GroupService()
